import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges } from '@angular/core';

import { GridViewGroupingConfiguration } from '@cds/betting-offer/grouping/grid-view';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { ShowcaseConfig } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { includes, isEmpty, keyBy, mapValues, zipObject } from 'lodash-es';

import { OptionGroupService } from '../../event-model/helpers/option-group.service';
import { OptionGroupDetail, ViewType } from '../showcase.model';
import { ShowcaseService } from '../showcase.service';

@Component({
    selector: 'ms-option-group-container',
    templateUrl: 'option-group-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class OptionGroupContainerComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: TrackingSource;
    @Input() gridGroup?: { [key: string]: string };
    @Input() groupingConfig?: GridViewGroupingConfiguration;

    viewType: ViewType;

    @HostBinding()
    get class(): string {
        return this.getHostClass();
    }

    private readonly MAX_OPTION_GROUPS = 3;
    showcaseOptionGroups: OptionGroupDetail[];

    constructor(
        private optionGroupService: OptionGroupService,
        private showcaseService: ShowcaseService,
        private showcaseConfig: ShowcaseConfig,
    ) {}

    ngOnChanges(changes: ISimpleChanges<OptionGroupContainerComponent>): void {
        if (changes.event) {
            this.viewType = includes(this.showcaseConfig.sixPackSportTypeIds, this.event.sport.id) ? ViewType.SixPack : ViewType.Default;
            this.mapOptionGroup();
        }
    }

    trackOptionGroup(index: number, optionGroup: OptionGroupDetail): string | number {
        return optionGroup.optionGroup?.gridGroups ? optionGroup.optionGroup.gridGroups.join(',') : index;
    }

    private mapOptionGroup(): void {
        if (isEmpty(this.gridGroup)) {
            this.showcaseOptionGroups = [];

            return;
        }
        const groupIds = this.showcaseService.getGridGroupIds(this.gridGroup);
        let mappedGroups: MappedGroup = {};

        if (this.viewType === ViewType.Default && this.groupingConfig?.groups.length) {
            const matchingGroups = keyBy(
                this.groupingConfig.groups.filter((g) => includes(groupIds, g.id)),
                (g) => g.id,
            );
            mappedGroups = mapValues(matchingGroups, (group) => ({ title: group.name, optionNames: group.optionNames }));
        } else if (this.viewType === ViewType.SixPack && this.groupingConfig?.sixPackGroups.length) {
            const matchingGroup = this.groupingConfig.sixPackGroups.filter((g) => this.hasMatchingGroupId(g.marketGroupIds, groupIds));
            if (matchingGroup.length) {
                const group = matchingGroup[0];
                const matchedGroup = zipObject(group.marketGroupIds, group.optionNames);
                mappedGroups = mapValues(matchedGroup, (value) => ({ title: value, optionNames: [] }));
            }
        }

        this.setOptionGroup(groupIds, mappedGroups);
    }

    private hasMatchingGroupId(marketGroupIds: string[], groupIds: string[]): boolean {
        return marketGroupIds.filter((mid) => includes(groupIds, mid)).length === groupIds.length;
    }

    private setOptionGroup(groupIds: string[], mappedGroups: MappedGroup): void {
        const optionGroups = groupIds.map((groupId) => {
            return {
                name:
                    this.showcaseConfig.nextGoalMarketGroupId === groupId
                        ? this.event.optionGroups.find((og) => og.gridGroups && og.gridGroups.some((g) => g === groupId))?.name
                        : mappedGroups[groupId]?.title || '',
                optionGroup: this.optionGroupService.get(this.event.optionGroups, this.event.sport.id, this.event.live, groupId),
                optionNames: mappedGroups[groupId]?.optionNames || [],
            };
        });

        this.showcaseOptionGroups = optionGroups.filter((v) => v.optionGroup).slice(0, this.MAX_OPTION_GROUPS);
    }

    private getHostClass(): string {
        return this.viewType === ViewType.SixPack ? 'six-pack-market-view' : 'default-market-view';
    }
}

export interface MappedGroup {
    [groupId: string]: {
        title: string;
        optionNames: string[];
    };
}
